<template>
  <div class="name">
    <StepHeader
      :title="title"
      :theme="theme"
      :shrink-mobile-header="isPersonalLoan"
    />

    <StepContent :theme="theme">
      <AppInputWithValidation
        v-for="(field, index) in fields"
        :key="index"
        :label="field.label"
        :name="field.name"
        :value="field.value"
        :rules="field.rules"
        :type="field.type"
        :hide-error="!submitted"
        :theme="theme"
        :error="field.error"
        :enterkeyhint="field.enterkeyhint"
        @input="$_input_updateState(field.vuexAction, $event)"
      />
    </StepContent>

    <StepFooter hide-mobile>
      <AppButton
        :theme="theme"
        :loading="registering"
        @click="handleSubmit"
      >
        Continue
      </AppButton>
    </StepFooter>
  </div>
</template>

<script>
import AppButton from '@/components/common/buttons/AppButton'
import AppInputWithValidation from '@/components/common/inputs/AppInputWithValidation'
import StepContent from '@/components/common/step-layout/StepContent'
import StepFooter from '@/components/common/step-layout/StepFooter'
import StepHeader from '@/components/common/step-layout/StepHeader'
import * as ROUTE_PATHS from '@/constants/route-paths'
import input from '@/mixins/input'
import navigation from '@/mixins/navigation'
import submitLoans from '@/mixins/submit/submit-loans'
import theme from '@/mixins/theme'
import tracking from '@/mixins/tracking'
import thirdPartyLogin from '@/mixins/third-party-login'
import CryptoJS from 'crypto-js'

export default {
  name: 'Name',

  components: {
    AppButton,
    AppInputWithValidation,
    StepContent,
    StepFooter,
    StepHeader
  },

  mixins: [
    input,
    navigation,
    submitLoans,
    theme,
    tracking,
    thirdPartyLogin
  ],

  props: {
    title: {
      type: String,
      default: 'What’s Your Name?'
    },

    enableInProgress: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      consent: false,
      registering: false
    }
  },

  computed: {
    firstName () {
      return this.$store.state.customer.first_name
    },

    lastName () {
      return this.$store.state.customer.last_name
    },

    firstNameError () {
      return this.$store.state.validations.firstNameError
    },

    lastNameError () {
      return this.$store.state.validations.lastNameError
    },

    email () {
      return this.$store.state.customer.email
    },

    fields () {
      return [{
        label: 'First Name',
        name: 'first_name',
        value: this.firstName,
        rules: 'required:First Name|min:2|max:30',
        vuexAction: 'setFirstName',
        error: this.firstNameError,
        enterkeyhint: 'next'
      }, {
        label: 'Last Name',
        name: 'last_name',
        value: this.lastName,
        rules: 'required:Last Name|min:2|max:30',
        vuexAction: 'setLastName',
        error: this.lastNameError,
        enterkeyhint: 'go'
      }]
    },

    dataLayerObject () {
      return {
        event: `spring_${this.eventPrefix}_register_account_continue`,
        sha256_first_name: CryptoJS.SHA256(this.firstName).toString(),
        sha256_last_name: CryptoJS.SHA256(this.lastName).toString(),
        sha256_email_address: CryptoJS.SHA256(this.email).toString()
      }
    },

    isPersonalLoan () {
      return this.$store.getters['loans/isPersonalLoan']
    },

    leadType () {
      return this.$store.state.returningCustomer.Lead_Type__c
    },

    agreementAccepted () {
      return this.$store.state.returningCustomer.Agreement_Accepted__c
    },

    plDeclinedHarvestScreen () {
      return this.$store.state.returningCustomer.Harvest_Page_Landed__c
    },

    cduid () {
      return this.$store.state.application.cduid
    },

    fbc () {
      return this.$store.state.tracking.fbc
    },

    fbp () {
      return this.$store.state.tracking.fbp
    }
  },

  watch: {
    $_navigation_triggerContinue: async function (val) {
      if (val) await this.handleSubmit()
      this.$store.dispatch('setTriggerContinue', false)
    }
  },

  created () {
    const { firstName, lastName } = this.$route.query
    if (firstName) this.$_input_updateState('setFirstName', firstName)
    if (lastName) this.$_input_updateState('setLastName', lastName)
  },

  mounted () {
    if (this.email) this.storeTempEmail()
  },

  methods: {
    async handleSubmit () {
      this.submitted = true
      if (this.registering) return false
      const isValid = await this.$_navigation_validateStep()
      if (!isValid) return
      try {
        this.registering = true
        if (!this.$_submit_leadCreated) {
          if (this.eventPrefix === 'ploan') {
            window.rudderanalytics.identify(this.email, {
              fbc: this.fbc || null,
              fbp: this.fbp || null,
              first_name: this.firstName,
              last_name: this.lastName,
              email: this.email
            })
          }
          await this.$_loans_createLead(this.dataLayerObject, null, this.enableInProgress)
        } else {
          // Check if returning customer already submitted
          if (this.$_submit_returningCustomerSubmitted) {
            this.$_loans_returningCustomerHandleSubmitted()
            return
          }

          // If declined flow customer navigated back, navigate back to the /try-foundation flow
          if (this.leadType === 'Declined PL - Foundation') {
            let path
            if (this.agreementAccepted) {
              path = `${ROUTE_PATHS.PL_DECLINED}/${ROUTE_PATHS.APPLICATION_SUCCESS}`
            } else if (!this.plDeclinedHarvestScreen) {
              path = `${ROUTE_PATHS.PL_DECLINED}`
            } else {
              path = `${ROUTE_PATHS.PL_DECLINED}/${ROUTE_PATHS.WELCOME_BACK}`
            }
            this.$router.push({ path })
            return
          }

          // If navigated back just go to next step
          this.$_navigation_handleNext()
        }
      } catch (error) {
        console.warn(error)
      } finally {
        this.registering = false
      }
    },

    storeTempEmail () {
      const email = this.email
      const trackingData = this.$store.state.tracking
      if (email) this.$_submit_storeTempEmail(email, trackingData)
    }
  }
}
</script>

<style scoped lang="scss">

</style>
